import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { greeting, longDate, usePortal } from "./PortalContext";
import { MessageTagButton } from "./MessageTagButton";

export default function PortalHome({ base }) {
  const portal = usePortal();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const response = await portal.get("/home");
      setData(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [portal]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="cp-error" data-testid="portal-home-error">{error}</p>;
  if (!data) return <div className="cp-loading" data-testid="portal-home-loading">Loading…</div>;

  const openItem = (item) => navigate(`${base}/evaluations?open=${item.id}${item.action === "upload_consent" ? "&consent=1" : ""}`);

  return (
    <div className="cp-page" data-testid="portal-home">
      <p className="cp-eyebrow" data-testid="portal-home-date">{longDate(data.today)}</p>
      <h1 className="cp-h1" data-testid="portal-home-greeting">{greeting()}, {data.greeting_name}</h1>
      <p className="cp-lede">Here is where your evaluations and staffing stand today.</p>

      <div className="cp-tiles">
        {data.tiles.map((tile) => (
          <Link key={tile.key} to={`${base}/evaluations?tab=${{ open: "open", due_soon: "due_soon", ready: "completed", consent: "awaiting_consent" }[tile.key]}`} className={`cp-tile tone-${tile.tone}`} data-testid={`portal-tile-${tile.key}`}>
            <strong data-testid={`portal-tile-${tile.key}-value`}>{tile.value}</strong>
            <span>{tile.label}</span>
            <small>{tile.detail}</small>
          </Link>
        ))}
      </div>

      <div className="cp-home-grid">
        <section className="cp-card" data-testid="portal-attention">
          <h2>Needs your attention</h2>
          {data.attention.length === 0 && <p className="cp-muted" data-testid="portal-attention-empty">Nothing needs your attention right now. TAG will let you know when something does.</p>}
          <ul className="cp-attention-list">
            {data.attention.map((item) => (
              <li key={`${item.id}-${item.action}`} data-testid={`portal-attention-${item.id}`}>
                <div>
                  <b>{item.title}</b>
                  <p>{item.detail}</p>
                </div>
                <button type="button" className={`cp-btn ${item.action === "upload_consent" ? "cp-btn-primary" : "cp-btn-outline"}`} data-testid={`portal-attention-action-${item.id}`} onClick={() => openItem(item)}>{item.action_label}</button>
              </li>
            ))}
          </ul>
        </section>

        <aside className="cp-side">
          <section className="cp-card" data-testid="portal-coming-up">
            <h2>Coming up</h2>
            {data.coming_up.length === 0 && <p className="cp-muted">No evaluations are due in the coming weeks.</p>}
            <ul className="cp-coming-list">
              {data.coming_up.map((row) => (
                <li key={row.id}><span><b>{row.student}</b> · {row.evaluation_type}</span><em>{row.due_label}</em></li>
              ))}
            </ul>
            <Link to={`${base}/evaluations`} className="cp-text-link" data-testid="portal-view-all-evaluations">View all evaluations</Link>
          </section>

          <section className="cp-card" data-testid="portal-team-today">
            <h2>Your team today</h2>
            <p><b>{data.team_today.on_site} of {data.team_today.assigned}</b> scheduled staff on site</p>
            <p className="cp-muted">{data.team_today.out_today.length ? `Out today: ${data.team_today.out_today.join(", ")}` : "Everyone assigned is in today."}</p>
            <Link to={`${base}/team`} className="cp-text-link" data-testid="portal-see-team">See your TAG team</Link>
          </section>

          <section className="cp-card cp-card-tint" data-testid="portal-contact-card">
            <p className="cp-eyebrow">Your TAG contact</p>
            <b className="cp-contact-name" data-testid="portal-contact-name">{data.tag_contact.name}</b>
            <p className="cp-muted" data-testid="portal-contact-email">{data.tag_contact.email}</p>
            <MessageTagButton className="cp-btn cp-btn-outline cp-btn-block" label="Message TAG" testId="portal-message-tag" />
          </section>
        </aside>
      </div>
    </div>
  );
}
